import React, { useState } from 'react';
import { X, Bold, Italic, Underline, List, Link as LinkIcon, Image } from 'lucide-react';

interface CreateNoticeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateNoticeModal: React.FC<CreateNoticeModalProps> = ({ isOpen, onClose }) => {
  const [status, setStatus] = useState('Draft');
  const [noticeTitle, setNoticeTitle] = useState('');
  const [shortMessage, setShortMessage] = useState('');
  const [fullContent, setFullContent] = useState('');
  const [audience, setAudience] = useState('');
  const [priority, setPriority] = useState('');
  const [allowDismissal, setAllowDismissal] = useState(true);
  const [sendNotification, setSendNotification] = useState(false);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg">
        {/* Header */}
        <div className="sticky top-0 bg-gray-100 px-6 py-4 flex items-center justify-between border-b border-gray-300">
          <div>
            <p className="text-xs uppercase tracking-wider text-gray-600 mb-1">CONTENT &gt; NOTICES</p>
            <h2 className="text-xl font-bold text-gray-900">Create New Notice</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {/* Status Tabs */}
          <div className="flex gap-6 border-b border-gray-200 pb-4">
            {['Draft', 'Scheduled', 'Published'].map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={`px-1 pb-2 text-sm font-medium transition-colors relative ${
                  status === s
                    ? 'text-gray-900 border-b-2 border-teal-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          {/* NOTICE STATUS Label */}
          <div>
            <p className="text-xs uppercase font-bold text-gray-600 tracking-wider mb-3">Notice Status</p>
            <div className="text-sm text-gray-700">{status}</div>
          </div>

          {/* Basic Information */}
          <div className="border-l-4 border-l-teal-600 pl-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Basic Information</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase font-bold text-gray-600 tracking-wider block mb-2">
                  Notice Title
                </label>
                <input
                  type="text"
                  placeholder="e.g. System Maintenance Update"
                  value={noticeTitle}
                  onChange={(e) => setNoticeTitle(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="text-xs uppercase font-bold text-gray-600 tracking-wider block mb-2">
                  Short Message Summary
                </label>
                <textarea
                  placeholder="Brief summary for notification previews..."
                  value={shortMessage}
                  onChange={(e) => setShortMessage(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>
          </div>

          {/* Detailed Content */}
          <div className="border-l-4 border-l-teal-600 pl-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Detailed Content</h3>
            <div>
              <label className="text-xs uppercase font-bold text-gray-600 tracking-wider block mb-2">
                Full Content (Rich Text)
              </label>
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <div className="bg-gray-100 p-2 border-b border-gray-300 flex gap-2">
                  <button className="p-1 hover:bg-gray-200 rounded"><Bold className="w-4 h-4" /></button>
                  <button className="p-1 hover:bg-gray-200 rounded"><Italic className="w-4 h-4" /></button>
                  <button className="p-1 hover:bg-gray-200 rounded"><Underline className="w-4 h-4" /></button>
                  <button className="p-1 hover:bg-gray-200 rounded"><List className="w-4 h-4" /></button>
                  <button className="p-1 hover:bg-gray-200 rounded"><LinkIcon className="w-4 h-4" /></button>
                  <button className="p-1 hover:bg-gray-200 rounded"><Image className="w-4 h-4" /></button>
                </div>
                <textarea
                  placeholder="Type the full notice content here. Support for rich text, links, and embedded assets..."
                  value={fullContent}
                  onChange={(e) => setFullContent(e.target.value)}
                  rows={8}
                  className="w-full px-4 py-2 focus:outline-none resize-none"
                />
              </div>
            </div>
          </div>

          {/* Targeting & Priority */}
          <div className="border-l-4 border-l-teal-600 pl-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Targeting & Priority</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs uppercase font-bold text-gray-600 tracking-wider block mb-2">
                  Audience Segment
                </label>
                <select
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option>Select Audience</option>
                  <option>Customers</option>
                  <option>Providers</option>
                  <option>Internal</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase font-bold text-gray-600 tracking-wider block mb-2">
                  Priority Level
                </label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option>Select Priority</option>
                  <option>High</option>
                  <option>Medium</option>
                  <option>Low</option>
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label className="text-xs uppercase font-bold text-gray-600 tracking-wider block mb-3">
                Display Placement
              </label>
              <div className="space-y-2">
                {['Dashboard banner', 'In-app popup', 'Login screen', 'Push notification'].map((placement) => (
                  <label key={placement} className="flex items-center gap-3">
                    <input type="checkbox" className="w-4 h-4" />
                    <span className="text-sm text-gray-700">{placement}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Publishing Schedule */}
          <div className="border-l-4 border-l-teal-600 pl-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Publishing Schedule</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs uppercase font-bold text-gray-600 tracking-wider block mb-2">
                  Activation Date & Time
                </label>
                <input
                  type="text"
                  placeholder="mm/dd/yyyy, --:-- --"
                  className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="text-xs uppercase font-bold text-gray-600 tracking-wider block mb-2">
                  Expiry Date & Time
                </label>
                <input
                  type="text"
                  placeholder="mm/dd/yyyy, --:-- --"
                  className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>
          </div>

          {/* Interaction Rules */}
          <div className="border-l-4 border-l-teal-600 pl-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Interaction Rules</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-900">Allow user dismissal</p>
                  <p className="text-sm text-gray-600">Users can close the notice after reading</p>
                </div>
                <button
                  onClick={() => setAllowDismissal(!allowDismissal)}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    allowDismissal ? 'bg-teal-600' : 'bg-gray-300'
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full bg-white transition-transform ${
                      allowDismissal ? 'translate-x-6' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-900">Send notification on publish</p>
                  <p className="text-sm text-gray-600">Trigger a global system notification when activated</p>
                </div>
                <button
                  onClick={() => setSendNotification(!sendNotification)}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    sendNotification ? 'bg-teal-600' : 'bg-gray-300'
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full bg-white transition-transform ${
                      sendNotification ? 'translate-x-6' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between">
          <p className="text-xs text-gray-600 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-teal-600"></span>
            FORM AUTOSAVED AT 10:42 AM
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              Save Draft
            </button>
            <button className="px-6 py-2 bg-teal-700 text-white rounded-lg font-medium hover:bg-teal-800 transition-colors">
              Publish Notice
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateNoticeModal;
